"use client"

import Banner from "@/components/header/banner"
import HeaderDetail from "@/components/header/header-detail"
import HeaderList from "@/components/header/header-list"
import HeaderSkeleton from "@/components/header/header-skeleton"
import { toImageSrc } from "@/lib/utils"
import { resolveBatchDataEntry, useBatchDataMap } from "@/lib/batch-data-context"
import { buildTamdaMediaHeaderKey } from "@/lib/extract-batch-requests"
import { ComponentConfig } from "@measured/puck"
import { useParams } from "next/navigation"
import { useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { useUIBuilderLocaleStore } from "../../../../store/useUIBuilderLocaleStore"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import { useStableComponentId } from "../TamdaMediaNewsLayout/hooks/useStableComponentId"

export interface TamdaMediaHeaderProps extends CommonStylesProps {
  bannerId: string
  bannerAltText: string
  bannerLink: string
  bannerTarget?: string
  bannerClickUrl?: string
  bannerImpressionHtml?: string
  bannerVendorScriptSrc?: string
  bannerVendorNoscriptHtml?: string
  showBanner: boolean
  contactPhone: string
  currencyRateText: string
  showHeaderDetail: boolean
  menuName: string
  locale?: string
  showHeaderList: boolean
  facebookLink: string
  youtubeLink: string
  facebookIcon: string
  youtubeIcon: string
}

export const RenderConfig: ComponentConfig<TamdaMediaHeaderProps> = {
  render: ({
    className,
    styles,
    responsiveType,
    bannerId,
    bannerAltText,
    bannerLink,
    bannerTarget,
    bannerClickUrl,
    bannerImpressionHtml,
    bannerVendorScriptSrc,
    bannerVendorNoscriptHtml,
    showBanner,
    contactPhone,
    currencyRateText,
    showHeaderDetail,
    menuName,
    showHeaderList,
    facebookLink,
    youtubeLink,
    facebookIcon,
    youtubeIcon,
    id: componentId,
  }) => {
    const params = useParams()
    const storeLocale = useUIBuilderLocaleStore(state => state.locale)
    const locale = (params?.locale as string) || storeLocale || "vi"
    const id = useStableComponentId(componentId)
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    const [isScrolled, setIsScrolled] = useState(false)
    const [modalSearch, setModalSearch] = useState(false)
    const batchData = useBatchDataMap()
    const headerEntry = resolveBatchDataEntry<{
      data?: Array<{ menuData?: any; currencyRate?: string; currentDate?: string }>
    }>(batchData, {
      key: buildTamdaMediaHeaderKey(id),
      prefix: "tamda-media-header:",
    })
    const headerSettings = headerEntry?.data?.[0]
    const loading = (showHeaderDetail || showHeaderList) && !headerSettings

    useEffect(() => {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 50)
      }

      if (typeof window !== "undefined") {
        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
      }
    }, [])

    // Build tracking config
    let impressionHtmlParsed = undefined
    if (bannerImpressionHtml) {
      try {
        impressionHtmlParsed = JSON.parse(bannerImpressionHtml)
      } catch (error) {
        console.warn("Invalid bannerImpressionHtml JSON:", error)
      }
    }

    const trackingConfig = (bannerClickUrl || impressionHtmlParsed || bannerVendorScriptSrc || bannerVendorNoscriptHtml) ? {
      clickUrl: bannerClickUrl || undefined,
      impressionHtml: impressionHtmlParsed,
      vendorScriptSrc: bannerVendorScriptSrc || undefined,
      vendorNoscriptHtml: bannerVendorNoscriptHtml || undefined,
    } : undefined

    const bannerData = {
      id: bannerId ? parseInt(bannerId) : undefined,
      imageUrl: "/images/banner-header.jpg",
      altText: bannerAltText || "Tamda Media",
      link: bannerLink || "",
      target: bannerTarget || "_blank",
      trackingConfig,
    }

    const headerDetailData = {
      currentDate: headerSettings?.currentDate || "",
      currencyRate: currencyRateText || headerSettings?.currencyRate || "24.27",
      contactInfo: {
        phone: contactPhone || "+420 771 268 888",
      },
      socialLinks: {
        facebook: facebookLink || "https://facebook.com",
        youtube: youtubeLink || "https://youtube.com",
        facebookIcon: facebookIcon ? toImageSrc(facebookIcon) : "",
        youtubeIcon: youtubeIcon ? toImageSrc(youtubeIcon) : "",
      },
    }

    if (loading) {
      return (
        <div className={twMerge(className)}>
          <HeaderSkeleton />
        </div>
      )
    }

    return (
      <>
        {responsiveCSS}
        <div className={twMerge(className)} id={id}>
          {showBanner && <Banner data={bannerData} bannerId={bannerId} />}
          {showHeaderDetail && <HeaderDetail data={headerDetailData} />}
          {showHeaderList && headerSettings?.menuData && (
            <HeaderList
              menuData={headerSettings.menuData}
              locale={locale}
              shouldBeTransparent={false}
              isScrolled={isScrolled}
              onSearchClick={() => setModalSearch(true)}
              socialLinks={{
                facebook: facebookLink || headerDetailData.socialLinks.facebook,
                youtube: youtubeLink || headerDetailData.socialLinks.youtube,
                facebookIcon: headerDetailData.socialLinks.facebookIcon,
                youtubeIcon: headerDetailData.socialLinks.youtubeIcon,
              }}
            />
          )}
        </div>
      </>
    )
  },
}
